import axios from "axios";
import { Alert } from "@texada/ui";
import i18n from "i18n/i18n";
import HttpStatus from "http-status-codes";
import { IFilterComp } from "ag-grid-community";
import {
  FLASK_ERROR_CODES,
  ALERT_ERROR_TIMEOUT
} from "../constants/AppConstants";
import { GATEWAY_ERROR_CODES } from "../constants/AppConstants";
import { GATEWAY_AUTH_URL } from "pages/gateway/urls";
import { logout } from "../actions";
import store from "../reduxstore/reduxStore";

/**
  target     --> class the function is on
  prop       --> the name of the property
  descriptor --> property data descriptor

  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperty#Description
*/
export function flaskReqErrorHandler(
  target: any,
  prop: string,
  descriptor: PropertyDescriptor
) {
  const original = descriptor.value;
  let valueToReturn;
  let errorMsg = "";

  descriptor.value = async (...args) => {
    if (!args[0].headers) {
      args[0].headers = {};
    }
    let showFlashMessage = true;
    let flashMessage = "";

    if (args.length > 1) {
      showFlashMessage =
        args[1]?.showMessage === undefined ? true : args[1]?.showMessage;
      flashMessage = args[1]?.message;
    }
    args[0].headers.Authorization = `Bearer ${localStorage.flaskToken}`;
    let result = await original
      .apply(this, args)
      .then(res => {
        if (
          ["put", "post", "delete"].includes(args[0].method) &&
          showFlashMessage
        ) {
          Alert.success({
            message: flashMessage || i18n.t("operation_successful")
          });
        }
        return res;
      })
      .catch(async error => {
        if (axios.isCancel(error)) return;

        const { response } = error;
        if (
          response?.data?.errorCode === FLASK_ERROR_CODES.AUTH_INVALID_TOKEN
        ) {
          localStorage.setItem(
            "login-error",
            i18n.t("your_session_has_expired_please_contact_texada_software")
          );
          store.dispatch(logout());
        } else if (response && response.status === HttpStatus.UNAUTHORIZED) {
          try {
            const tokenResp = await axios.get("/rest-api/getflasktoken");
            args[0].headers.Authorization = `Bearer ${tokenResp.data.flask_token}`;
            localStorage.setItem("flaskToken", tokenResp.data.flask_token);
            valueToReturn = await original.apply(this, args);
          } catch (error) {
            if (error?.response?.status === HttpStatus.UNAUTHORIZED) {
              store.dispatch(logout());
            }
            throw error;
          }
        } else {
          if (showFlashMessage) {
            if (response?.data?.errorMsg) {
              errorMsg = response.data.errorMsg;
            } else {
              errorMsg = error.message;
            }

            Alert.error({
              message: errorMsg || i18n.t("an_unexpected_error_occurred"),
              timeout: ALERT_ERROR_TIMEOUT
            });
          }
          throw error;
        }
      });

    return result || valueToReturn;
  };
  return descriptor;
}

export function gatewayReqErrorHandler(
  target: any,
  prop: string,
  descriptor: PropertyDescriptor
) {
  const original = descriptor.value;
  let valueToReturn;
  let errorMsg = "";

  descriptor.value = async (...args) => {
    if (!args[0].headers) {
      args[0].headers = {};
    }
    let showFlashMessage = true;
    let flashMessage = "";

    if (args.length > 1) {
      showFlashMessage =
        args[1]?.showMessage === undefined ? true : args[1]?.showMessage;
      flashMessage = args[1]?.message;
    }

    args[0].headers.Authorization = localStorage.gatewayToken;
    let result = await original
      .apply(this, args)
      .then(res => {
        if (
          ["put", "post", "delete"].includes(args[0].method) &&
          showFlashMessage
        ) {
          Alert.success({
            message: flashMessage || i18n.t("operation_successful")
          });
        }
        return res;
      })
      .catch(async error => {
        if (axios.isCancel(error)) return;

        const { response } = error;
        if (
          response?.data?.errorCode === GATEWAY_ERROR_CODES.AUTH_INVALID_TOKEN
        ) {
          localStorage.setItem(
            "login-error",
            i18n.t("your_session_has_expired_please_contact_texada_software")
          );
          store.dispatch(logout());
        } else if (
          response &&
          [HttpStatus.UNAUTHORIZED, HttpStatus.FORBIDDEN].includes(
            response.status
          )
        ) {
          try {
            const tokenResp = await axios.get(GATEWAY_AUTH_URL);
            args[0].headers.Authorization = tokenResp.data.gateway_token;
            localStorage.setItem("gatewayToken", tokenResp.data.gateway_token);
            valueToReturn = await original.apply(this, args);
          } catch (error) {
            store.dispatch(logout());
            throw error;
          }
        } else {
          if (showFlashMessage) {
            if (response?.data?.errorMsg) {
              errorMsg = response.data.errorMsg;
            } else {
              errorMsg = error.message;
            }

            Alert.error({
              message: errorMsg || i18n.t("an_unexpected_error_occurred"),
              timeout: null
            });
          }
          throw error;
        }
      });

    return result || valueToReturn;
  };
  return descriptor;
}

export function checkFilter<T extends Omit<IFilterComp, "getGui">>(
  target: T,
  prop: string,
  descriptor: PropertyDescriptor
): void {
  const originalGetModel = descriptor.value;

  descriptor.value = function (this: T) {
    const isFilterActive = this.isFilterActive();
    return isFilterActive ? originalGetModel.apply(this) : undefined;
  };
}
